import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Home, Table2, Pin, PinOff, X, Shield } from "lucide-react";
import { useReducedMotion } from "../../hooks/useReducedMotion.ts";

export type ViewType = "overview" | "requests";

export interface PrimaryNavigationProps {
  activeView: ViewType;
  onSelectView: (view: ViewType) => void;
  isPinned?: boolean;
  onTogglePinned?: () => void;
  // Backward compatibility props
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
}

const FOCUSABLE_SELECTORS =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const PrimaryNavigation: React.FC<PrimaryNavigationProps> = ({
  activeView,
  onSelectView,
  isPinned: isPinnedProp,
  onTogglePinned: onTogglePinnedProp,
  isExpanded: isExpandedProp,
  onToggleExpanded: onToggleExpandedProp,
  isMobileOpen,
  onCloseMobile,
}) => {
  const prefersReducedMotion = useReducedMotion();

  // Support both isPinned/onTogglePinned and legacy isExpanded/onToggleExpanded
  const isPinned = isPinnedProp ?? isExpandedProp ?? false;
  const onTogglePinned = onTogglePinnedProp ?? onToggleExpandedProp ?? (() => {});

  // Hover & focus state for automatic desktop expansion
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  // Hover delay timers: ~100ms entry delay, ~200ms exit delay
  const entryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const drawerRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Desktop expansion state: pinned open, hovered, or keyboard-focused
  const isExpanded = isPinned || isHovered || isFocused;

  // 1. Pointer Entry: ~100ms delay to prevent accidental triggers; cancels exit timer
  const handleMouseEnter = useCallback(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    if (isHovered) return;

    if (!entryTimerRef.current) {
      entryTimerRef.current = setTimeout(() => {
        setIsHovered(true);
        entryTimerRef.current = null;
      }, 100);
    }
  }, [isHovered]);

  // 2. Pointer Leave: ~200ms exit delay to prevent flickering; cancels entry timer
  const handleMouseLeave = useCallback(() => {
    if (entryTimerRef.current) {
      clearTimeout(entryTimerRef.current);
      entryTimerRef.current = null;
    }
    if (!exitTimerRef.current) {
      exitTimerRef.current = setTimeout(() => {
        setIsHovered(false);
        exitTimerRef.current = null;
      }, 200);
    }
  }, []);

  // 3. Keyboard Focus: Expand immediately when focus moves inside
  const handleFocus = useCallback(() => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
    setIsFocused(true);
  }, []);

  // 4. Keyboard Blur: Collapse only when focus leaves the navigation entirely
  const handleBlur = useCallback((e: React.FocusEvent<HTMLElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsFocused(false);
  }, []);

  // Timer cleanups on unmount
  useEffect(() => {
    return () => {
      if (entryTimerRef.current) clearTimeout(entryTimerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, []);

  // Timer cleanups on view transitions
  useEffect(() => {
    if (entryTimerRef.current) clearTimeout(entryTimerRef.current);
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
  }, [activeView]);

  // Timer cleanups and state reset on viewport changes
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleViewportChange = () => {
      if (entryTimerRef.current) clearTimeout(entryTimerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
      setIsHovered(false);
      setIsFocused(false);
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleViewportChange);
      return () => mediaQuery.removeEventListener("change", handleViewportChange);
    } else {
      mediaQuery.addListener(handleViewportChange);
      return () => mediaQuery.removeListener(handleViewportChange);
    }
  }, []);

  // Auto-close mobile drawer if viewport crosses into desktop width (md: 768px)
  useEffect(() => {
    if (!isMobileOpen || typeof window === "undefined" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    if (mediaQuery.matches) {
      onCloseMobile();
      return;
    }

    const handleChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        onCloseMobile();
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [isMobileOpen, onCloseMobile]);

  // Focus management & Escape key handling for mobile drawer
  useEffect(() => {
    if (!isMobileOpen) {
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
      return;
    }

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const timeout = setTimeout(() => {
      closeBtnRef.current?.focus();
    }, 50);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseMobile();
        return;
      }

      if (e.key === "Tab" && drawerRef.current) {
        const focusable = Array.from(
          drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
        ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileOpen, onCloseMobile]);

  const handleMobileSelect = useCallback(
    (view: ViewType) => {
      onSelectView(view);
      onCloseMobile();
    },
    [onSelectView, onCloseMobile]
  );

  return (
    <>
      {/*
        Desktop In-Flow Sidebar:
        Layout width matches visible width (48px collapsed, 192px expanded/pinned),
        flowing naturally in flex shell alongside workspace without overlaying it.
      */}
      <aside
        aria-label="Primary Navigation"
        aria-expanded={isExpanded}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`hidden md:flex flex-col justify-between shrink-0 h-full border-r border-border bg-panel py-3 select-none overflow-hidden ${
          prefersReducedMotion
            ? ""
            : "transition-[width] duration-200 ease-out"
        } ${isExpanded ? "w-48" : "w-12"}`}
      >
        {/* Top: Nav Destination Buttons */}
        <div className="space-y-1.5 w-full flex flex-col items-center">
          {/* Overview button */}
          <button
            type="button"
            onClick={() => onSelectView("overview")}
            aria-label="Overview"
            title={!isExpanded ? "Overview" : undefined}
            className={`group h-10 w-[calc(100%-12px)] mx-1.5 rounded-md flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand overflow-hidden ${
              activeView === "overview"
                ? "bg-panel-active text-white border border-border shadow-xs"
                : "text-foreground-muted hover:text-foreground hover:bg-panel-hover"
            }`}
          >
            {/* Fixed-position icon container (exactly 36px wide, centered in 48px rail) */}
            <div className="w-9 h-9 shrink-0 flex items-center justify-center">
              <Home className="w-4 h-4 shrink-0" />
            </div>
            {/* Label: fades and slides slightly into view */}
            <span
              className={`whitespace-nowrap text-sm font-medium ${
                prefersReducedMotion
                  ? isExpanded
                    ? "opacity-100"
                    : "opacity-0 w-0 pointer-events-none"
                  : "transition-all duration-200 ease-out"
              } ${
                isExpanded
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-1.5 w-0 pointer-events-none"
              }`}
            >
              Overview
            </span>
          </button>

          {/* Requests button */}
          <button
            type="button"
            onClick={() => onSelectView("requests")}
            aria-label="Requests"
            title={!isExpanded ? "Requests" : undefined}
            className={`group h-10 w-[calc(100%-12px)] mx-1.5 rounded-md flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand overflow-hidden ${
              activeView === "requests"
                ? "bg-panel-active text-white border border-border shadow-xs"
                : "text-foreground-muted hover:text-foreground hover:bg-panel-hover"
            }`}
          >
            {/* Fixed-position icon container */}
            <div className="w-9 h-9 shrink-0 flex items-center justify-center">
              <Table2 className="w-4 h-4 shrink-0" />
            </div>
            {/* Label */}
            <span
              className={`whitespace-nowrap text-sm font-medium ${
                prefersReducedMotion
                  ? isExpanded
                    ? "opacity-100"
                    : "opacity-0 w-0 pointer-events-none"
                  : "transition-all duration-200 ease-out"
              } ${
                isExpanded
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-1.5 w-0 pointer-events-none"
              }`}
            >
              Requests
            </span>
          </button>
        </div>

        {/* Bottom: Pin / Unpin Accessible Toggle Button */}
        <div className="pt-2 border-t border-border/40 w-full flex flex-col items-center">
          <button
            type="button"
            onClick={onTogglePinned}
            aria-label={isPinned ? "Unpin navigation" : "Pin navigation open"}
            aria-pressed={isPinned}
            title={isPinned ? "Unpin navigation" : "Pin navigation open"}
            className={`group h-10 w-[calc(100%-12px)] mx-1.5 rounded-md flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand overflow-hidden ${
              isPinned
                ? "bg-brand/10 text-brand border border-brand/30"
                : "text-foreground-muted hover:text-foreground hover:bg-panel-hover"
            }`}
          >
            <div className="w-9 h-9 shrink-0 flex items-center justify-center">
              {isPinned ? (
                <PinOff className="w-4 h-4 shrink-0" />
              ) : (
                <Pin className="w-4 h-4 shrink-0" />
              )}
            </div>
            <span
              className={`whitespace-nowrap text-xs font-medium ${
                prefersReducedMotion
                  ? isExpanded
                    ? "opacity-100"
                    : "opacity-0 w-0 pointer-events-none"
                  : "transition-all duration-200 ease-out"
              } ${
                isExpanded
                  ? "opacity-100 translate-x-0"
                  : "opacity-0 -translate-x-1.5 w-0 pointer-events-none"
              }`}
            >
              {isPinned ? "Unpin navigation" : "Pin open"}
            </span>
          </button>
        </div>
      </aside>

      {/* Mobile Navigation Drawer Overlay (on narrow screens) */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.15 }}
              onClick={onCloseMobile}
              className="fixed inset-0 bg-black/60 z-40 md:hidden"
              aria-hidden="true"
            />

            {/* Slide-out drawer */}
            <motion.div
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
              tabIndex={-1}
              initial={{ x: prefersReducedMotion ? 0 : "-100%", opacity: prefersReducedMotion ? 0 : 1 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: prefersReducedMotion ? 0 : "-100%", opacity: prefersReducedMotion ? 0 : 1 }}
              transition={{ duration: prefersReducedMotion ? 0 : 0.18, ease: "easeOut" }}
              className="fixed top-0 left-0 h-full w-64 bg-panel border-r border-border z-50 flex flex-col focus:outline-none shadow-2xl md:hidden"
            >
              {/* Drawer Header */}
              <div className="h-12 border-b border-border px-4 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-6 h-6 rounded bg-brand-subtle text-brand border border-brand-border">
                    <Shield className="w-4 h-4" />
                  </div>
                  <span className="text-sm font-medium text-foreground">Navigation</span>
                </div>
                <button
                  ref={closeBtnRef}
                  type="button"
                  onClick={onCloseMobile}
                  aria-label="Close navigation menu"
                  className="p-1.5 rounded-md text-foreground-muted hover:text-foreground hover:bg-panel-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Drawer Navigation Links */}
              <div className="p-3 space-y-1.5 flex-grow">
                <button
                  type="button"
                  onClick={() => handleMobileSelect("overview")}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md w-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                    activeView === "overview"
                      ? "bg-panel-active text-white border border-border"
                      : "text-foreground-muted hover:text-foreground hover:bg-panel-hover"
                  }`}
                >
                  <Home className="w-4 h-4 shrink-0" />
                  <span>Overview</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleMobileSelect("requests")}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md w-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${
                    activeView === "requests"
                      ? "bg-panel-active text-white border border-border"
                      : "text-foreground-muted hover:text-foreground hover:bg-panel-hover"
                  }`}
                >
                  <Table2 className="w-4 h-4 shrink-0" />
                  <span>Requests</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
